import React from 'react';
import { Company, Customer, CheckoutPricing } from '@/types';
import { CustomerSearchPanel } from './CustomerSearchPanel';
import { PatientDetailsForm } from './PatientDetailsForm';
import { BookingTypePanel } from './BookingTypePanel';
import { ManualPaymentPanel } from './ManualPaymentPanel';

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  dob: string;
  jobTitle: string;
}

interface CardDetails {
  number: string;
  expiry: string;
  cvc: string;
  cardholderName: string;
}

interface BillingAddress {
  address1: string;
  city: string;
  postalCode: string;
  country: string;
}

interface BookingPatientStepProps {
  // Customer search
  customerSearch: string;
  onCustomerSearchChange: (value: string) => void;
  filteredCustomers: Customer[];
  isCustomerSearchLoading: boolean;
  customerSearchError: string;
  selectedCustomerId: string | null;
  onSelectExistingCustomer: (customer: Customer) => void;
  onCreateNewCustomer: () => void;
  // Customer details form
  customerDetails: CustomerDetails;
  onCustomerDetailsChange: (details: CustomerDetails) => void;
  dobTouched: boolean;
  onDobTouched: () => void;
  emailTouched: boolean;
  onEmailTouched: () => void;
  phoneTouched: boolean;
  onPhoneTouched: () => void;
  showDobError: boolean;
  showEmailError: boolean;
  showPhoneError: boolean;
  currentYear: number;
  // Booking type
  isCorporate: boolean;
  onIsCorporateChange: (value: boolean) => void;
  paymentMethod: 'card' | 'invoice';
  onPaymentMethodChange: (method: 'card' | 'invoice') => void;
  // Company
  companySearch: string;
  onCompanySearchChange: (value: string) => void;
  isCompanyDropdownOpen: boolean;
  onCompanyDropdownOpen: () => void;
  onCompanyDropdownClose: () => void;
  filteredCompanies: Company[];
  isCompanySearchLoading: boolean;
  companySearchError: string;
  selectedCompanyId: string;
  selectedCompany: Company | undefined;
  onSelectCompany: (id: string, name: string) => void;
  poNumber: string;
  onPoNumberChange: (value: string) => void;
  // Card
  cardDetails: CardDetails;
  onCardDetailsChange: (details: CardDetails) => void;
  billingAddress: BillingAddress;
  onBillingAddressChange: (address: BillingAddress) => void;
  pricing: CheckoutPricing;
  hasService: boolean;
}

export const BookingPatientStep: React.FC<BookingPatientStepProps> = ({
  customerSearch,
  onCustomerSearchChange,
  filteredCustomers,
  isCustomerSearchLoading,
  customerSearchError,
  selectedCustomerId,
  onSelectExistingCustomer,
  onCreateNewCustomer,
  customerDetails,
  onCustomerDetailsChange,
  dobTouched,
  onDobTouched,
  emailTouched,
  onEmailTouched,
  phoneTouched,
  onPhoneTouched,
  showDobError,
  showEmailError,
  showPhoneError,
  currentYear,
  isCorporate,
  onIsCorporateChange,
  paymentMethod,
  onPaymentMethodChange,
  companySearch,
  onCompanySearchChange,
  isCompanyDropdownOpen,
  onCompanyDropdownOpen,
  onCompanyDropdownClose,
  filteredCompanies,
  isCompanySearchLoading,
  companySearchError,
  selectedCompanyId,
  selectedCompany,
  onSelectCompany,
  poNumber,
  onPoNumberChange,
  cardDetails,
  onCardDetailsChange,
  billingAddress,
  onBillingAddressChange,
  pricing,
  hasService
}) => (
  <div className="space-y-6">
    <div className="max-w-2xl mx-auto">
      <CustomerSearchPanel
        customerSearch={customerSearch}
        onCustomerSearchChange={onCustomerSearchChange}
        filteredCustomers={filteredCustomers}
        isCustomerSearchLoading={isCustomerSearchLoading}
        customerSearchError={customerSearchError}
        selectedCustomerId={selectedCustomerId}
        selectedCustomerName={customerDetails.name}
        selectedCustomerEmail={customerDetails.email}
        onSelectExistingCustomer={onSelectExistingCustomer}
        onCreateNewCustomer={onCreateNewCustomer}
      />

      <PatientDetailsForm
        customerDetails={customerDetails}
        selectedCustomerId={selectedCustomerId}
        dobTouched={dobTouched}
        onDobTouched={onDobTouched}
        emailTouched={emailTouched}
        onEmailTouched={onEmailTouched}
        phoneTouched={phoneTouched}
        onPhoneTouched={onPhoneTouched}
        showDobError={showDobError}
        showEmailError={showEmailError}
        showPhoneError={showPhoneError}
        currentYear={currentYear}
        onCustomerDetailsChange={onCustomerDetailsChange}
      />

      <BookingTypePanel
        isCorporate={isCorporate}
        onIsCorporateChange={onIsCorporateChange}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={onPaymentMethodChange}
        companySearch={companySearch}
        onCompanySearchChange={onCompanySearchChange}
        isCompanyDropdownOpen={isCompanyDropdownOpen}
        onCompanyDropdownOpen={onCompanyDropdownOpen}
        onCompanyDropdownClose={onCompanyDropdownClose}
        filteredCompanies={filteredCompanies}
        isCompanySearchLoading={isCompanySearchLoading}
        companySearchError={companySearchError}
        selectedCompanyId={selectedCompanyId}
        selectedCompany={selectedCompany}
        onSelectCompany={onSelectCompany}
        poNumber={poNumber}
        onPoNumberChange={onPoNumberChange}
      />

      {paymentMethod === 'card' && (
        <ManualPaymentPanel
          hasService={hasService}
          cardDetails={cardDetails}
          onCardDetailsChange={onCardDetailsChange}
          billingAddress={billingAddress}
          onBillingAddressChange={onBillingAddressChange}
          pricing={pricing}
        />
      )}
    </div>
  </div>
);
